import { useCallback, useId, useState, type ReactElement } from "react";
import { errorText } from "../lib/api";
import { up } from "../lib/tr";
import { Dialog } from "./Dialog";
import { useServices } from "./Services";
import { TurnstileBox } from "./TurnstileBox";

const MAX = 280;

/** "Bildir": reason (plain text, ≤280) + Turnstile → POST /api/v1/reports. */
export function ReportButton({ target, label }: { target: { type: "topic" | "comment"; id: string }; label: string }): ReactElement {
  const { api } = useServices();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const fieldId = useId();
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  const length = Array.from(reason.trim()).length;

  const send = () => {
    if (!token || length === 0 || length > MAX) return;
    setState("sending");
    setError(null);
    api.report(target, reason.trim(), token).then(
      () => {
        setState("sent");
      },
      (err: unknown) => {
        setError(errorText(err));
        setState("idle");
        setToken(null); // tokens are single-use
      },
    );
  };

  return (
    <>
      <button
        type="button"
        className="z-linkbtn z-report"
        onClick={() => {
          setOpen(true);
          setState("idle");
          setError(null);
          setToken(null);
        }}
        aria-label={`Bildir: ${label}`}
      >
        {up("bildir")}
      </button>
      <Dialog open={open} title="Bildir" onClose={close}>
        {state === "sent" ? (
          <p role="status" className="z-mono">
            {up("bildirim alındı")}. Teşekkürler; yeterince bildirim gelirse içerik akıştan çekilir.
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <p className="z-small">Hedef kişiler değil, şeyler ve durumlardır. Bu içerik kuralı çiğniyorsa kısaca yaz.</p>
            <label htmlFor={fieldId} className="z-label">
              {up("neden")}
            </label>
            <textarea
              id={fieldId}
              className="z-textarea"
              value={reason}
              maxLength={MAX * 2}
              rows={3}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              aria-describedby={`${fieldId}-count`}
            />
            <p id={`${fieldId}-count`} className={`z-small${length > MAX ? " is-over" : ""}`}>
              {length} / {MAX} — düz metin, bağlantı yok.
            </p>
            {token === null ? <TurnstileBox onToken={setToken} /> : <p className="z-small">Doğrulandı.</p>}
            {error && (
              <p role="alert" className="z-small">
                {error}
              </p>
            )}
            <button type="submit" className="z-btn" disabled={!token || length === 0 || length > MAX || state === "sending"}>
              {up(state === "sending" ? "gönderiliyor" : "bildir")}
            </button>
          </form>
        )}
      </Dialog>
    </>
  );
}
